import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import { VerificationStatus, VerificationStatusInfo } from 'enums/provider.enum';
import TextField from 'common/forms/TextField';
import {
  validateEmail,
  validatePhoneNumber,
  validateValueWithRegex,
} from 'common/forms/formValidations';
import { addPatientProvider } from 'store/thunks/patientThunks';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { validateProviderApi } from 'api/practice/index';

const AddProviderDialog = ({ open, onClose, practiceId, mpi, onProviderAdded }) => {
  const dispatch = useDispatch();
  const [providerDataToSave, setProviderDataToSave] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  const [npiValidationModal, setNpiValidationModal] = useState({
    open: false,
    rejectionReason: null,
    verificationStatus: null,
    existingProvider: null,
  });

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setProviderDataToSave({});
      setLoading(false);
      onClose();
    }
  };

  const closeNpiValidationModal = () => {
    setNpiValidationModal({
      open: false,
      rejectionReason: null,
      verificationStatus: null,
      existingProvider: null,
    });
    setLoading(false);
  };

  // POM-6360: no provider address is collected; the practice address is used for the prescriber
  const saveProvider = (formData) => {
    const providerPayload = mpi ? { mpi, practiceId, ...formData } : { practiceId, ...formData };

    return dispatch(addPatientProvider({ provider: providerPayload })).then((success) => {
      if (success) {
        handleCloseModal();
        if (onProviderAdded) {
          onProviderAdded();
        }
        setProviderDataToSave({});
      } else if (formRef.current) {
        formRef.current.initialize(formData);
      }
    });
  };

  const handleAddProvider = (formData) => {
    setLoading(true);
    const { firstName, lastName, npi } = formData;

    const validateProviderInput = {
      npi,
      firstName,
      lastName,
      practiceId,
    };

    setProviderDataToSave(formData);

    return validateProviderApi({ input: validateProviderInput })
      .then((validationResponse) => {
        const { verificationStatus, rejectionReason, existingProvider } = validationResponse;
        const isError =
          verificationStatus === VerificationStatus.NPI_INVALID ||
          verificationStatus === VerificationStatus.PROVIDER_NPI_EXISTING_IN_SYSTEM ||
          verificationStatus === VerificationStatus.PROVIDER_NPI_EXISTING_IN_PRACTICE ||
          verificationStatus === VerificationStatus.FAILED_TO_VALIDATE ||
          verificationStatus === VerificationStatus.FAILED ||
          verificationStatus === VerificationStatus.NAME_MISMATCH ||
          verificationStatus === VerificationStatus.UNVERIFIED;
        if (isError) {
          setNpiValidationModal({
            open: true,
            rejectionReason,
            verificationStatus,
            existingProvider,
          });
          if (formRef.current) {
            formRef.current.initialize(formData);
          }
          return undefined;
        }

        return saveProvider(formData);
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        if (formRef.current) {
          formRef.current.initialize(formData);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {npiValidationModal.open && (
        <Dialog
          open={npiValidationModal.open}
          onClose={closeNpiValidationModal}
          maxWidth='sm'
          fullWidth
          aria-labelledby='npi-validation-modal-title'
        >
          <DialogTitle id='npi-validation-modal-title'>
            {npiValidationModal.verificationStatus
              ? VerificationStatusInfo[npiValidationModal.verificationStatus]
              : 'Validation Error'}
          </DialogTitle>
          <DialogContent>
            <Typography variant='body1'>
              {npiValidationModal.verificationStatus ===
                VerificationStatus.PROVIDER_NPI_EXISTING_IN_SYSTEM &&
              npiValidationModal.existingProvider ? (
                <>
                  {npiValidationModal.rejectionReason && (
                    <Typography sx={{ mb: 2 }}>{npiValidationModal.rejectionReason}</Typography>
                  )}
                  <Typography>
                    Did you mean this provider:{' '}
                    <strong>
                      {npiValidationModal.existingProvider.firstName}{' '}
                      {npiValidationModal.existingProvider.middleName
                        ? `${npiValidationModal.existingProvider.middleName} `
                        : ''}
                      {npiValidationModal.existingProvider.lastName}
                    </strong>
                    ?
                  </Typography>
                  <Typography sx={{ mt: 1 }}>If not, please add a new NPI.</Typography>
                </>
              ) : (
                npiValidationModal.rejectionReason || 'An error occurred during validation.'
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant='contained' onClick={closeNpiValidationModal}>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={open}
        onClose={handleCloseModal}
        aria-describedby='WorkQueue-AddPracticeProviderDialog-header'
        fullWidth
      >
        <DialogTitle id='WorkQueue-AddPracticeProviderDialog-header'>Add Provider</DialogTitle>

        <Form
          onSubmit={handleAddProvider}
          initialValues={providerDataToSave}
          render={({ handleSubmit, invalid, form }) => {
            if (formRef.current !== form) {
              formRef.current = form;
            }
            return (
              <form noValidate onSubmit={handleSubmit}>
                <DialogContent>
                  <TextField
                    id='WorkQueue-AddPracticeProviderDialog-firstName-input'
                    label='First Name'
                    name='firstName'
                    required
                    autoFocus
                  />

                  <TextField
                    id='WorkQueue-AddPracticeProviderDialog-lastName-input'
                    label='Last Name'
                    name='lastName'
                    required
                  />

                  <TextField
                    id='WorkQueue-AddPracticeProviderDialog-phoneNumber-input'
                    label='Phone Number'
                    name='phone'
                    required
                    validations={[validatePhoneNumber()]}
                  />

                  <TextField
                    id='WorkQueue-AddPracticeProviderDialog-email-input'
                    label='Email'
                    name='email'
                    validations={[validateEmail()]}
                  />

                  <TextField
                    id='WorkQueue-AddPracticeProviderDialog-npi-input'
                    label='NPI'
                    name='npi'
                    required
                    validations={[validateValueWithRegex('Must be 10 numbers', /^\d{10}$/)]}
                  />

                  <TextField
                    id='WorkQueue-AddPracticeProviderDialog-fax-input'
                    label='Fax'
                    name='fax'
                    validations={[validatePhoneNumber('Invalid Fax Number')]}
                  />
                </DialogContent>

                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    variant='contained'
                    disabled={invalid}
                    type='submit'
                  >
                    Save
                  </LoadingButton>
                </DialogActions>
              </form>
            );
          }}
        />
      </Dialog>
    </>
  );
};

AddProviderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  practiceId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  onProviderAdded: PropTypes.func.isRequired,
};

export default AddProviderDialog;
